import type {
  ITeacherDemoDiagnosticLocal,
  ITeacherDemoDiagnosticSample,
  ITeacherDemoDiagnosticTrack,
  ITeacherDemoDiagnosticsBatch,
} from '~/types/teacher-application'

export type DemoNetworkSample = ITeacherDemoDiagnosticSample & { key: string }
export interface DemoNetworkSummary {
  samples: DemoNetworkSample[]
  sampleCount: number
  measuredSampleCount: number
  warningSampleCount: number
  /** Number of sampled RECONNECTING states, not distinct reconnect events. */
  reconnectSampleCount: number
  firstAt?: number
  lastAt?: number
  maxRttMs?: number
  maxPacketLossRatio?: number
  maxJitterMs?: number
}

const metric = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : undefined
const ratio = (value: unknown): number | undefined => {
  const result = metric(value)
  return result !== undefined && result <= 1 ? result : undefined
}
const quality = (value: unknown): number | undefined => {
  const result = metric(value)
  return result !== undefined && Number.isInteger(result) && result >= 1 && result <= 6 ? result : undefined
}
const maximum = (values: Array<number | null | undefined>): number | undefined => {
  const valid = values.map(metric).filter((value): value is number => value !== undefined)
  return valid.length ? Math.max(...valid) : undefined
}
const tracks = (sample: ITeacherDemoDiagnosticSample) => Array.isArray(sample.tracks) ? sample.tracks : []
const abnormalConnection = (sample: ITeacherDemoDiagnosticSample) =>
  ['RECONNECTING', 'DISCONNECTING', 'DISCONNECTED'].includes(sample.connection)

/** Ratios remain in 0..1; percentage formatting belongs to the view. */
export function samplePacketLossRatio(sample: ITeacherDemoDiagnosticSample): number | undefined {
  return maximum([ratio(sample.localAudio?.packetLossRatio), ratio(sample.localVideo?.packetLossRatio),
    ...tracks(sample).map(track => ratio(track?.packetLossRatio))])
}

export function sampleJitterMs(sample: ITeacherDemoDiagnosticSample): number | undefined {
  return maximum([metric(sample.localAudio?.jitterMs), metric(sample.localVideo?.jitterMs)])
}

/** Include the screen-sharing connection when present, matching the summary and warning rule. */
export function sampleRttMs(sample: ITeacherDemoDiagnosticSample): number | undefined {
  return maximum([metric(sample.rttMs), metric(sample.screenRttMs)])
}

export function isWarningSample(sample: ITeacherDemoDiagnosticSample): boolean {
  const rtt = sampleRttMs(sample)
  const loss = samplePacketLossRatio(sample)
  const level = maximum([quality(sample.uplinkQuality), quality(sample.downlinkQuality)])
  return abnormalConnection(sample) || (rtt !== undefined && rtt >= 400) ||
    (loss !== undefined && loss >= .05) || (level !== undefined && level >= 4)
}

const cleanLocal = (value: ITeacherDemoDiagnosticLocal | null | undefined): ITeacherDemoDiagnosticLocal | undefined =>
  value ? { sendBitrateBps: metric(value.sendBitrateBps), packetLossRatio: ratio(value.packetLossRatio),
    jitterMs: metric(value.jitterMs), frameRate: metric(value.frameRate) } : undefined
const cleanTrack = (value: ITeacherDemoDiagnosticTrack): ITeacherDemoDiagnosticTrack => ({
  uid: value.uid, kind: value.kind, endToEndMs: metric(value.endToEndMs), transportMs: metric(value.transportMs),
  jitterBufferMs: metric(value.jitterBufferMs), bitrateBps: metric(value.bitrateBps), packetLossRatio: ratio(value.packetLossRatio),
  freezeRatio: ratio(value.freezeRatio), freezeSeconds: metric(value.freezeSeconds), frameRate: metric(value.frameRate),
})

function cleanSample(sample: ITeacherDemoDiagnosticSample, key: string): DemoNetworkSample {
  return {
    key, at: sample.at, connection: sample.connection,
    rttMs: metric(sample.rttMs), edgeDelayMs: metric(sample.edgeDelayMs), screenRttMs: metric(sample.screenRttMs),
    sendBitrateBps: metric(sample.sendBitrateBps), receiveBitrateBps: metric(sample.receiveBitrateBps),
    screenSendBitrateBps: metric(sample.screenSendBitrateBps),
    uplinkQuality: quality(sample.uplinkQuality), downlinkQuality: quality(sample.downlinkQuality),
    localAudio: cleanLocal(sample.localAudio), localVideo: cleanLocal(sample.localVideo),
    tracks: tracks(sample).filter(track => track != null).map(cleanTrack),
  }
}

function hasNetworkMeasurement(sample: ITeacherDemoDiagnosticSample): boolean {
  if (abnormalConnection(sample)) return true
  const values = [sample.rttMs, sample.edgeDelayMs, sample.screenRttMs, sample.sendBitrateBps,
    sample.receiveBitrateBps, sample.screenSendBitrateBps, quality(sample.uplinkQuality), quality(sample.downlinkQuality),
    samplePacketLossRatio(sample), sampleJitterMs(sample), sample.localAudio?.sendBitrateBps, sample.localVideo?.sendBitrateBps,
    ...tracks(sample).flatMap(track => [track.endToEndMs, track.transportMs, track.jitterBufferMs, track.bitrateBps])]
  return values.some(value => metric(value) !== undefined)
}

export function buildDemoNetworkSummary(batches?: ITeacherDemoDiagnosticsBatch[] | null): DemoNetworkSummary {
  const seen = new Set<string>()
  const samples: DemoNetworkSample[] = []
  for (const batch of Array.isArray(batches) ? batches : []) {
    if (!batch || !Array.isArray(batch.samples)) continue
    const batchKey = `${batch.sessionId}:${batch.sequence}`
    if (seen.has(batchKey)) continue
    seen.add(batchKey)
    batch.samples.forEach((sample, index) => {
      if (!sample || metric(sample.at) === undefined) return
      samples.push(cleanSample(sample, `${batchKey}:${index}`))
    })
  }
  samples.sort((a, b) => a.at - b.at || (a.key < b.key ? -1 : a.key > b.key ? 1 : 0))
  return {
    samples, sampleCount: samples.length,
    measuredSampleCount: samples.filter(hasNetworkMeasurement).length,
    warningSampleCount: samples.filter(isWarningSample).length,
    reconnectSampleCount: samples.filter(sample => sample.connection === 'RECONNECTING').length,
    firstAt: samples[0]?.at, lastAt: samples[samples.length - 1]?.at,
    maxRttMs: maximum(samples.map(sampleRttMs)),
    maxPacketLossRatio: maximum(samples.map(samplePacketLossRatio)),
    maxJitterMs: maximum(samples.map(sampleJitterMs)),
  }
}
